/**
 * Browser pop-up notifier — fires `new Notification()` for unseen reminder
 * notifications whose owner opted into the browser-push channel.
 *
 * The personal-event jobRunner tick on the server tags those notifications
 * with type === 'reminder_push' (or 'reminder_push_only' when the user also
 * disabled the bell). This poller filters on those types, fires once per
 * notification id (tracked in localStorage), and is a no-op when:
 *   - the browser doesn't support Notification
 *   - permission isn't 'granted'
 *   - the user isn't authenticated
 *
 * Polling cadence matches the bell-drawer cadence (60s) so we don't double
 * the request rate. We do an immediate fetch on boot and on visibilitychange
 * (so reminders fire promptly when the user returns to the tab).
 */

import { client } from '../api/client.js';

const POLL_MS = 60_000;
const SHOWN_KEY = 'heist.browser_push.shown_ids';
const SHOWN_LIMIT = 200;
const MATCH_TYPES = new Set(['reminder_push', 'reminder_push_only']);

let timer = null;
let inflight = null;

function readShown() {
    try {
        const raw = localStorage.getItem(SHOWN_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr : []);
    } catch (_) {
        return new Set();
    }
}

function persistShown(set) {
    try {
        // Trim to most-recent SHOWN_LIMIT to keep localStorage bounded.
        const arr = Array.from(set).slice(-SHOWN_LIMIT);
        localStorage.setItem(SHOWN_KEY, JSON.stringify(arr));
    } catch (_) {}
}

async function tick() {
    if (inflight) return inflight;
    if (!window.__auth__) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    inflight = (async () => {
        try {
            const res = await client.getNotifications();
            const items = (res && res.notifications) || [];
            if (!items.length) return;

            const shown = readShown();
            let mutated = false;

            for (const n of items) {
                if (!MATCH_TYPES.has(n.type)) continue;
                if (shown.has(n.id)) continue;

                try {
                    new Notification(n.title || 'Heist reminder', {
                        body: n.body || '',
                        tag: `heist-reminder-${n.id}`,   // dedup if user has multiple tabs
                        icon: '/favicon.png',
                    });
                } catch (_) {
                    // Some browsers throw if window isn't focused/permitted; ignore.
                }
                shown.add(n.id);
                mutated = true;
            }
            if (mutated) persistShown(shown);
        } catch (err) {
            // Silent — the bell drawer surfaces network errors already.
        } finally {
            inflight = null;
        }
    })();
    return inflight;
}

export function startBrowserNotifier() {
    if (timer) return;
    // Kick once immediately, then on a steady cadence.
    tick();
    timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') tick();
    });
}

export function stopBrowserNotifier() {
    if (timer) { clearInterval(timer); timer = null; }
}
